import { useRef, useState } from "react";
import { useFiles, useUploadFile, useDeleteFile, useFileDownloadUrl } from "@platform/hooks";
import { PageHeader } from "../components/shared/PageHeader";
import { Button, Input, Label, useToast } from "@nthamizh/ui";
import { Upload, Download, Trash2, FileText, MoreVertical, Link2, Hash, FolderOpen, ChevronRight } from "lucide-react";

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Groups a flat file list by folderPath, returning an ordered list of
 *  [folderPath, files[]] pairs sorted so "/" is always first, then
 *  alphabetically. */
function groupByFolder(files: { folderPath: string; [k: string]: unknown }[]): [string, typeof files][] {
  const map = new Map<string, typeof files>();
  for (const f of files) {
    const key = f.folderPath || "/";
    const bucket = map.get(key) ?? [];
    bucket.push(f);
    map.set(key, bucket);
  }
  return [...map.entries()].sort(([a], [b]) => {
    if (a === "/") return -1;
    if (b === "/") return 1;
    return a.localeCompare(b);
  });
}

function folderLabel(path: string): string {
  return path === "/" ? "My files" : path;
}

export function MyFilesPage() {
  const { toast } = useToast();
  const { data, isLoading } = useFiles();
  const uploadFile = useUploadFile();
  const deleteFile = useDeleteFile();
  const getDownloadUrl = useFileDownloadUrl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());

  // Upload state — folder picker
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingFolder, setPendingFolder] = useState("/");
  const [customFolder, setCustomFolder] = useState("");

  const files = data?.files ?? [];
  const usedBytes = data?.usedBytes ?? 0;
  const quotaBytes = data?.quotaBytes ?? 1;
  const usagePercent = Math.min(100, (usedBytes / quotaBytes) * 100);

  const folderGroups = groupByFolder(files);
  const existingFolders = folderGroups.map(([p]) => p).filter((p) => p !== "/");

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPendingFile(file);
    setPendingFolder("/");
    setCustomFolder("");
  };

  const handleUploadConfirm = async () => {
    if (!pendingFile) return;
    const folderPath = pendingFolder === "__custom__"
      ? (customFolder.trim() || "/")
      : pendingFolder;
    try {
      const contentBase64 = await readFileAsBase64(pendingFile);
      await uploadFile.mutateAsync({
        fileName: pendingFile.name,
        mimeType: pendingFile.type || "application/octet-stream",
        contentBase64,
        folderPath: folderPath.startsWith("/") ? folderPath : `/${folderPath}`,
      });
      toast({ title: `${pendingFile.name} uploaded`, variant: "success" });
      setPendingFile(null);
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Upload failed", variant: "error" });
    }
  };

  const handleDownload = async (id: string) => {
    try {
      await getDownloadUrl.mutateAsync(id);
      // The hook itself triggers the browser save-as — nothing else to do here
    } catch {
      toast({ title: "Couldn't download this file", variant: "error" });
    }
  };

  const handleCopyLink = async (id: string) => {
    // Authenticated deep link — requires the recipient to be logged in
    // as this same user. Uses the API's own public base URL (same as
    // what the rest of the app uses for API calls) so it works correctly
    // across all subdomains (app.tsnet.dev, acme.tsnet.dev, etc.)
    // without having to infer the API domain from the current window URL.
    const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
    const url = `${apiBase}/v1/files/${id}/download`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied — recipient must be logged in to use", variant: "success" });
    } catch {
      toast({ title: "Couldn't copy a link for this file", variant: "error" });
    }
  };

  const handleDelete = async (id: string) => {
    if (confirmingDeleteId !== id) {
      setConfirmingDeleteId(id);
      setTimeout(() => setConfirmingDeleteId((c) => (c === id ? null : c)), 3000);
      return;
    }
    await deleteFile.mutateAsync(id);
    setConfirmingDeleteId(null);
    setOpenMenuId(null);
  };

  const toggleFolder = (path: string) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) { next.delete(path); } else { next.add(path); }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="My files"
        subtitle="Your own storage — only you can see or access these files."
        action={
          <>
            <input ref={fileInputRef} type="file" onChange={handleFileSelected} className="hidden" />
            <Button onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-4 h-4" /> Upload
            </Button>
          </>
        }
      />

      {/* Upload folder picker */}
      {pendingFile && (
        <div className="bg-white rounded-[12px] border border-ds-border p-5 space-y-3">
          <p className="text-sm font-semibold text-ds-text">
            Upload <span className="font-mono text-xs bg-ds-bg px-1.5 py-0.5 rounded">{pendingFile.name}</span>
          </p>
          <div className="space-y-1.5">
            <Label>Save to folder</Label>
            <select
              value={pendingFolder}
              onChange={(e) => setPendingFolder(e.target.value)}
              className="w-full h-10 rounded-lg border border-ds-border px-3 text-sm bg-white"
            >
              <option value="/">/ (root)</option>
              {existingFolders.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
              <option value="/reports/">/reports/</option>
              <option value="__custom__">New folder…</option>
            </select>
            {pendingFolder === "__custom__" && (
              <Input
                placeholder="/my-folder/"
                value={customFolder}
                onChange={(e) => setCustomFolder(e.target.value)}
                className="font-mono"
              />
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setPendingFile(null)}>Cancel</Button>
            <Button onClick={handleUploadConfirm} loading={uploadFile.isPending}>Upload</Button>
          </div>
        </div>
      )}

      {/* Storage bar */}
      <div className="bg-white rounded-[12px] border border-ds-border p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-ds-text">Storage used</p>
          <p className="text-sm text-ds-text-secondary">{formatBytes(usedBytes)} / {formatBytes(quotaBytes)}</p>
        </div>
        <div className="h-2 bg-ds-bg rounded-full overflow-hidden">
          <div
            className={`h-full ${usagePercent >= 90 ? "bg-red-500" : "bg-indigo-500"}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
      </div>

      {isLoading && <p className="text-sm text-ds-text-muted">Loading…</p>}

      {!isLoading && files.length === 0 && (
        <div className="bg-white rounded-[12px] border border-ds-border p-10 text-center">
          <FileText className="w-8 h-8 text-ds-text-muted mx-auto mb-2" />
          <p className="text-sm text-ds-text-secondary">No files yet. Upload one to get started.</p>
        </div>
      )}

      {/* Folder groups */}
      {!isLoading && folderGroups.map(([folderPath, folderFiles]) => {
        const collapsed = collapsedFolders.has(folderPath);
        return (
          <div key={folderPath} className="bg-white rounded-[12px] border border-ds-border">
            {/* Folder header */}
            <button
              onClick={() => toggleFolder(folderPath)}
              className="w-full flex items-center gap-2 px-5 py-3 hover:bg-ds-bg/60 transition-colors rounded-[12px]"
            >
              <FolderOpen className="w-4 h-4 text-indigo-400 shrink-0" />
              <span className="text-sm font-semibold text-ds-text flex-1 text-left">{folderLabel(folderPath)}</span>
              <span className="text-xs text-ds-text-muted">{folderFiles.length} file{folderFiles.length !== 1 ? "s" : ""}</span>
              <ChevronRight className={`w-4 h-4 text-ds-text-muted transition-transform ${collapsed ? "" : "rotate-90"}`} />
            </button>

            {/* File rows */}
            {!collapsed && (
              <div className="divide-y divide-ds-border border-t border-ds-border">
                {(folderFiles as { id: string; fileName: string; mimeType: string; sizeBytes: number; folderPath: string; createdAt: string }[]).map((file) => (
                  <div key={file.id} className="flex items-center gap-3 px-5 py-3.5">
                    <FileText className="w-5 h-5 text-ds-text-muted shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ds-text truncate">{file.fileName}</p>
                      <p className="text-xs text-ds-text-muted mt-0.5">
                        {formatBytes(file.sizeBytes)} · {new Date(file.createdAt).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Three-dots menu */}
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuId((id) => (id === file.id ? null : file.id))}
                        className="p-2 rounded-lg text-ds-text-secondary hover:bg-ds-bg hover:text-ds-text transition-colors"
                        title="More"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {openMenuId === file.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                          <div className="absolute right-0 top-full mt-1 z-20 w-44 bg-white rounded-lg border border-ds-border shadow-card py-1">
                            <button
                              onClick={() => { setOpenMenuId(null); void handleDownload(file.id); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ds-text hover:bg-ds-bg text-left"
                            >
                              <Download className="w-3.5 h-3.5" /> Download
                            </button>
                            <button
                              onClick={() => { setOpenMenuId(null); void handleCopyLink(file.id); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ds-text hover:bg-ds-bg text-left"
                            >
                              <Link2 className="w-3.5 h-3.5" /> Copy link
                            </button>
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                navigator.clipboard.writeText(file.id).then(
                                  () => toast({ title: "File ID copied", variant: "success" }),
                                  () => toast({ title: "Couldn't copy file ID", variant: "error" }),
                                );
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ds-text hover:bg-ds-bg text-left"
                            >
                              <Hash className="w-3.5 h-3.5" /> Copy file ID
                            </button>
                            <button
                              onClick={() => { void handleDelete(file.id); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                                confirmingDeleteId === file.id ? "text-red-700 bg-red-50" : "text-red-600 hover:bg-red-50"
                              }`}
                            >
                              <Trash2 className="w-3.5 h-3.5" /> {confirmingDeleteId === file.id ? "Click again to confirm" : "Delete"}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
